/**
 * AttachmentsSection — reusable attachment UI for jobs, deals, faults,
 * customers, and customer services.
 *
 * Props describe what entity you're looking at and where to propagate uploads.
 * The component handles:
 *   - Photo (camera) + File upload buttons with progress + error states
 *   - Thumbnail grid for photos + row list for files
 *   - Lightbox for photo viewing
 *   - Delete (X on hover) with confirm
 *   - "From fault/deal/job" source badge when viewing on a parent entity
 *
 * Read-only mode hides upload + delete affordances.
 */

import { getLocaleSync } from '@/utils/locale';
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Image as ImageIcon,
  Paperclip,
  Download,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  FileText,
  ExternalLink,
  Mail,
  Play,
} from 'lucide-react';
import {
  uploadAttachmentMedia,
  uploadAttachmentFile,
  deleteAttachment,
  formatFileSize,
  type PropagateRefs,
} from '@/services/attachment.service';
import type { Attachment, AttachmentSource } from '@/types/attachment.types';
import { EmailDetailModal } from './EmailDetailModal';
import { useEmailAttachController } from './EmailAttachContext';

interface Props {
  attachments: Attachment[];
  /** Where the CURRENT view is — uploads are attributed here as the source */
  source: AttachmentSource;
  /** Additional entities to propagate the upload to */
  propagate?: PropagateRefs;
  /**
   * The entity type the current view represents. When an attachment's
   * `source.type` differs from this, we show a "from ..." badge.
   */
  currentEntityType: AttachmentSource['type'];
  /** Disable upload + delete when true */
  readOnly?: boolean;
  /** Custom title — defaults to "Attachments" */
  title?: string;
  /** Hint for callers building compact views */
  compact?: boolean;
  /**
   * When provided, renders a close (X) button in the header — lets the section
   * be dropped straight into a modal as the panel body without a duplicate
   * card/header wrapper.
   */
  onClose?: () => void;
}

function AttachmentsSectionInner({
  attachments,
  source,
  propagate,
  currentEntityType,
  readOnly = false,
  title = 'Attachments',
  compact = false,
  onClose,
}: Props) {
  const photoInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<null | 'media' | 'file'>(null);
  // Batch progress across a multi-file selection ({ done, total }).
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<Attachment | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Attachment | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Wired up only when a parent EmailDropZone is mounted (currently the
  // DealWorkspacePage). When absent, the Email button + hint stay hidden so
  // we don't advertise a feature that won't work on this view.
  const emailCtrl = useEmailAttachController();

  // Sort newest first
  const sorted = [...attachments].sort((a, b) =>
    (b.timestamp || '').localeCompare(a.timestamp || ''),
  );

  const media = sorted.filter((a) => a.kind === 'photo' || a.kind === 'video');
  const files = sorted.filter((a) => a.kind === 'file');
  // Emails render on every entity view that supports attachments (deal,
  // fault, customer, customer_service). Jobs don't surface emails today.
  const emails =
    currentEntityType === 'job'
      ? []
      : sorted.filter((a) => a.kind === 'email');

  // Click-to-open email reader
  const [openEmail, setOpenEmail] = useState<Attachment | null>(null);

  const userId = localStorage.getItem('user_id') || localStorage.getItem('tech_id') || 'user';
  const userName =
    localStorage.getItem('logged_in_user') ||
    localStorage.getItem('tech_name') ||
    'User';

  // Layered Escape: close the topmost open layer first (lightbox → delete
  // confirm), and only call onClose (close the whole section/modal) when no
  // inner layer is open. Centralised here so the Lightbox doesn't need its own
  // listener that would otherwise double-fire with a parent modal's Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (lightbox) { setLightbox(null); return; }
      if (confirmDelete) { setConfirmDelete(null); return; }
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox, confirmDelete, onClose]);

  // Upload a batch of files one-by-one, collecting per-file failures so one
  // bad file (too large, wrong type) never sinks the whole selection.
  const uploadBatch = async (
    files: File[],
    kind: 'media' | 'file',
    upload: (file: File) => Promise<unknown>,
  ) => {
    if (!files.length) return;
    setError(null);
    setBusy(kind);
    setProgress({ done: 0, total: files.length });
    const failures: string[] = [];
    for (let i = 0; i < files.length; i++) {
      try {
        await upload(files[i]);
      } catch (err) {
        failures.push(
          `${files[i].name || 'file'}: ${err instanceof Error ? err.message : 'upload failed'}`,
        );
      }
      setProgress({ done: i + 1, total: files.length });
    }
    if (failures.length) {
      setError(
        failures.length === 1
          ? failures[0]
          : `${failures.length} of ${files.length} uploads failed. ${failures[0]}`,
      );
    }
    setBusy(null);
    setProgress(null);
  };

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    void uploadBatch(files, 'media', (file) =>
      uploadAttachmentMedia({ file, source, propagate, addedBy: userId, addedByName: userName }),
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    void uploadBatch(files, 'file', (file) =>
      uploadAttachmentFile({ file, source, propagate, addedBy: userId, addedByName: userName }),
    );
  };

  // Hand the picked .eml off to the parent EmailDropZone — it owns the
  // parse → preview → upload pipeline so the modal UX is identical
  // whether the user dragged or clicked.
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !emailCtrl) return;
    emailCtrl.trigger(file);
  };

  const handleDelete = async (a: Attachment) => {
    if (!a.source) {
      // Can't safely delete without knowing the source — shouldn't happen
      // for attachments uploaded via the new service, but be defensive.
      setError('Cannot delete — source entity unknown');
      return;
    }
    setDeleting(true);
    try {
      await deleteAttachment({
        attachmentId: a.id,
        source: a.source,
        propagate,
        storagePath: a.storagePath,
      });
      setConfirmDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const isBusy = !!busy;
  const hasItems = sorted.length > 0;

  return (
    <section className="rounded-2xl border border-theme bg-surface-card p-4 space-y-3">
      {/* Header + action pills. In modal mode (onClose set) the header sticks
          to the top of the scrolling panel so it stays reachable with many
          attachments. */}
      <div
        className={`flex items-center justify-between gap-2 ${
          onClose ? 'sticky top-0 z-20 -mx-4 -mt-4 px-4 pt-4 pb-3 bg-surface-card border-b border-theme-soft rounded-t-2xl' : ''
        }`}
      >
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-theme-primary">{title}</h3>
          {hasItems && (
            <span className="text-[11px] text-theme-muted">
              {sorted.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {!readOnly && (
            <>
              <ActionPill
                icon={
                  busy === 'media' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ImageIcon className="w-3.5 h-3.5" />
                  )
                }
                label={
                  busy === 'media' && progress
                    ? `${progress.done}/${progress.total}`
                    : 'Media'
                }
                onClick={() => photoInputRef.current?.click()}
                disabled={isBusy}
                tone="sky"
                filled
              />
              <ActionPill
                icon={
                  busy === 'file' ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Paperclip className="w-3.5 h-3.5" />
                  )
                }
                label={
                  busy === 'file' && progress
                    ? `${progress.done}/${progress.total}`
                    : 'Files'
                }
                onClick={() => fileInputRef.current?.click()}
                disabled={isBusy}
                tone="sky"
              />
              {emailCtrl && (
                <ActionPill
                  icon={<Mail className="w-3.5 h-3.5" />}
                  label="Email"
                  onClick={() => emailInputRef.current?.click()}
                  disabled={isBusy}
                  tone="gold"
                />
              )}
            </>
          )}
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="ml-1 inline-flex items-center justify-center w-11 h-11 rounded-lg text-theme-muted hover:text-theme-primary hover:bg-surface-hover transition-colors"
              aria-label="Close"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Drag-drop hint — only shown when the parent page wired up
          an EmailDropZone, so we never falsely advertise a feature. */}
      {emailCtrl && !readOnly && (
        <p className="text-[11px] text-theme-muted leading-snug">
          <Mail className="inline w-3 h-3 mr-1 -mt-0.5 text-brand-secondary" />
          Tip: drag a <span className="font-medium">.eml</span> from Gmail /
          Outlook anywhere on this page to attach it.
        </p>
      )}

      {/* Hidden file inputs. Media accepts photos AND videos, multi-select;
          no `capture` so the native sheet still offers the camera but also
          lets the user pick several from the library at once. */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={handleMediaChange}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={emailInputRef}
        type="file"
        accept=".eml,message/rfc822"
        className="hidden"
        onChange={handleEmailChange}
      />

      {/* Error */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 dark:text-red-400 text-xs"
          >
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="opacity-70 hover:opacity-100">
              <X className="w-3.5 h-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty state */}
      {!hasItems && (
        <div className="py-6 text-center">
          <Paperclip className="w-6 h-6 mx-auto text-theme-muted opacity-40" />
          <p className="mt-2 text-xs text-theme-muted">
            {readOnly ? 'No attachments yet.' : 'Attach photos, videos or files to get started.'}
          </p>
        </div>
      )}

      {/* Media grid (photos + videos) */}
      {media.length > 0 && (
        <div
          className={`grid gap-2 ${
            compact ? 'grid-cols-3' : 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5'
          }`}
        >
          {media.map((a) => (
            <MediaThumb
              key={a.id}
              attachment={a}
              onOpen={() => setLightbox(a)}
              onDelete={readOnly ? undefined : () => setConfirmDelete(a)}
              showSourceBadge={a.source?.type !== currentEntityType}
            />
          ))}
        </div>
      )}

      {/* Files list */}
      {files.length > 0 && (
        <div className="space-y-1.5">
          {files.map((a) => (
            <FileRow
              key={a.id}
              attachment={a}
              onDelete={readOnly ? undefined : () => setConfirmDelete(a)}
              showSourceBadge={a.source?.type !== currentEntityType}
            />
          ))}
        </div>
      )}

      {/* Emails */}
      {emails.length > 0 && (
        <div className="space-y-1.5">
          {emails.map((a) => (
            <EmailRow
              key={a.id}
              attachment={a}
              onOpen={() => setOpenEmail(a)}
              onDelete={readOnly ? undefined : () => setConfirmDelete(a)}
              showSourceBadge={a.source?.type !== currentEntityType}
            />
          ))}
        </div>
      )}

      {/* Email reader */}
      <AnimatePresence>
        {openEmail && (
          <EmailDetailModal
            attachment={openEmail}
            onClose={() => setOpenEmail(null)}
          />
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <Lightbox attachment={lightbox} onClose={() => setLightbox(null)} />
        )}
      </AnimatePresence>

      {/* Delete confirm */}
      <AnimatePresence>
        {confirmDelete && (
          <DeleteConfirm
            attachment={confirmDelete}
            busy={deleting}
            onConfirm={() => handleDelete(confirmDelete)}
            onCancel={() => setConfirmDelete(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export const AttachmentsSection = AttachmentsSectionInner;

// ============================================================================
// Sub-components
// ============================================================================

function ActionPill({
  icon,
  label,
  onClick,
  disabled,
  tone,
  filled = false,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone: 'sky' | 'gold';
  /** Filled = primary emphasis (e.g. Photo, the most common attach action). */
  filled?: boolean;
}) {
  const toneClass =
    tone === 'sky'
      ? filled
        ? 'bg-sky-500/20 border-sky-400/50 text-sky-700 dark:text-sky-300 hover:bg-sky-500/30 hover:border-sky-400/70'
        : 'border-sky-400/30 text-sky-600 dark:text-sky-400 hover:bg-sky-500/10 hover:border-sky-400/60'
      : tone === 'gold'
      ? 'border-brand-secondary/40 text-brand-secondary hover:bg-brand-secondary/10 hover:border-brand-secondary/70'
      : '';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-[12px] font-medium
        transition-all min-h-[36px]
        ${filled ? '' : 'bg-surface-card'}
        disabled:opacity-50 disabled:cursor-not-allowed
        ${toneClass}
      `}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function MediaThumb({
  attachment,
  onOpen,
  onDelete,
  showSourceBadge,
}: {
  attachment: Attachment;
  onOpen: () => void;
  onDelete?: () => void;
  showSourceBadge?: boolean;
}) {
  const isVideo = attachment.kind === 'video';
  return (
    <div className="relative group aspect-square rounded-lg overflow-hidden bg-surface-hover border border-theme">
      <button
        type="button"
        onClick={onOpen}
        className="absolute inset-0 w-full h-full"
        aria-label={isVideo ? 'Play video' : 'View photo'}
      >
        {isVideo ? (
          // preload metadata so the first frame paints as a poster; muted +
          // playsInline keeps mobile browsers from auto-fullscreening.
          <video
            src={attachment.url}
            className="w-full h-full object-cover pointer-events-none"
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <img
            src={attachment.thumbnailUrl || attachment.url}
            alt={attachment.caption || attachment.fileName || 'Photo'}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        )}
      </button>

      {/* Play overlay for videos */}
      {isVideo && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="w-9 h-9 rounded-full bg-black/55 flex items-center justify-center">
            <Play className="w-4 h-4 text-white fill-white ml-0.5" />
          </div>
        </div>
      )}

      {/* Source badge */}
      {showSourceBadge && attachment.source && (
        <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-black/60 text-white">
          {sourceLabel(attachment.source.type)}
        </div>
      )}

      {/* Delete button */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
          aria-label={isVideo ? 'Delete video' : 'Delete photo'}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function FileRow({
  attachment,
  onDelete,
  showSourceBadge,
}: {
  attachment: Attachment;
  onDelete?: () => void;
  showSourceBadge?: boolean;
}) {
  const size = formatFileSize(attachment.fileSize);
  return (
    <div className="group flex items-center gap-3 p-2.5 rounded-lg border border-theme bg-surface-hover">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center">
        <FileText className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-[13px] font-medium text-theme-primary truncate">
            {attachment.fileName || 'File'}
          </p>
          {showSourceBadge && attachment.source && (
            <span className="text-[9px] font-semibold uppercase tracking-wide text-theme-muted">
              from {sourceLabel(attachment.source.type)}
            </span>
          )}
        </div>
        <p className="text-[11px] text-theme-muted truncate">
          {size ? `${size} · ` : ''}
          {attachment.addedByName || attachment.addedBy}
        </p>
      </div>
      {attachment.url && (
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 p-1.5 rounded text-theme-secondary hover:bg-surface-card hover:text-theme-primary transition-colors"
          aria-label="Download file"
          title="Open"
        >
          <Download className="w-4 h-4" />
        </a>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          className="flex-shrink-0 p-1.5 rounded text-theme-muted opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500 hover:bg-red-500/10"
          aria-label="Delete file"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function EmailRow({
  attachment,
  onOpen,
  onDelete,
  showSourceBadge,
}: {
  attachment: Attachment;
  onOpen: () => void;
  onDelete?: () => void;
  showSourceBadge?: boolean;
}) {
  const direction =
    attachment.emailDirection === 'outbound' ? 'Sent' : 'Received';
  const dateIso = attachment.emailDate || attachment.timestamp;
  let dateLabel = '';
  try {
    dateLabel = new Date(dateIso).toLocaleDateString(getLocaleSync(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    dateLabel = dateIso;
  }
  const attachmentCount = attachment.emailAttachments?.length ?? 0;

  return (
    <div className="group relative rounded-lg border border-theme bg-surface-hover hover:border-brand-secondary/50 transition-colors">
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left p-3"
      >
        {/* Top row: direction + date + badges */}
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand-secondary/15 flex items-center justify-center flex-shrink-0">
            <Mail className="w-3.5 h-3.5 text-brand-secondary" />
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-secondary">
            {direction}
          </span>
          <span className="text-[10px] text-theme-muted">·</span>
          <span className="text-[10px] text-theme-muted">{dateLabel}</span>
          {showSourceBadge && attachment.source && (
            <>
              <span className="text-[10px] text-theme-muted">·</span>
              <span className="text-[10px] font-semibold uppercase tracking-wide text-theme-muted">
                from {sourceLabel(attachment.source.type)}
              </span>
            </>
          )}
          {attachmentCount > 0 && (
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-theme-muted">
              <Paperclip className="w-3 h-3" />
              {attachmentCount}
            </span>
          )}
        </div>

        {/* Subject */}
        <p className="text-[13px] font-semibold text-theme-primary truncate pl-9">
          {attachment.emailSubject || '(no subject)'}
        </p>

        {/* From / To compact summary */}
        <div className="pl-9 mt-0.5 space-y-0.5 text-[11px] text-theme-secondary">
          {attachment.emailFrom && (
            <p className="truncate">
              <span className="text-theme-muted">From:</span> {attachment.emailFrom}
            </p>
          )}
          {attachment.emailTo && (
            <p className="truncate">
              <span className="text-theme-muted">To:</span> {attachment.emailTo}
            </p>
          )}
          {attachment.emailCc && (
            <p className="truncate">
              <span className="text-theme-muted">Cc:</span> {attachment.emailCc}
            </p>
          )}
        </div>

        {/* Body preview */}
        {attachment.emailSummary && (
          <p className="pl-9 mt-1.5 text-[11px] text-theme-muted line-clamp-2">
            {attachment.emailSummary}
          </p>
        )}
      </button>

      {/* Delete */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute top-2 right-2 p-1.5 rounded text-theme-muted opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500 hover:bg-red-500/10"
          aria-label="Delete email"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function Lightbox({
  attachment,
  onClose,
}: {
  attachment: Attachment;
  onClose: () => void;
}) {
  // Escape is handled centrally by AttachmentsSectionInner (layered close) so
  // it doesn't double-fire with a parent modal's Escape handler.
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      {attachment.kind === 'video' ? (
        <video
          src={attachment.url}
          className="max-w-full max-h-full object-contain rounded"
          controls
          autoPlay
          playsInline
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img
          src={attachment.url}
          alt={attachment.caption || attachment.fileName || 'Photo'}
          className="max-w-full max-h-full object-contain rounded"
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {(attachment.caption || attachment.addedByName) && (
        <div className="absolute bottom-4 left-4 right-4 text-center text-white/80 text-xs">
          {attachment.caption && <p>{attachment.caption}</p>}
          {attachment.addedByName && (
            <p className="text-white/60 mt-0.5">
              {attachment.addedByName} ·{' '}
              {new Date(attachment.timestamp).toLocaleString(getLocaleSync(), {
                day: 'numeric',
                month: 'short',
                hour: 'numeric',
                minute: '2-digit',
              })}
            </p>
          )}
        </div>
      )}
      {attachment.url && (
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="absolute top-4 left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
          aria-label="Open in new tab"
          title="Open in new tab"
        >
          <ExternalLink className="w-5 h-5" />
        </a>
      )}
    </motion.div>
  );
}

function DeleteConfirm({
  attachment,
  busy,
  onConfirm,
  onCancel,
}: {
  attachment: Attachment;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[310] bg-black/70 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-surface-card rounded-xl border border-theme p-5 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-theme-primary">
          Delete{' '}
          {attachment.kind === 'photo'
            ? 'photo'
            : attachment.kind === 'video'
            ? 'video'
            : attachment.kind === 'email'
            ? 'email'
            : 'file'}
          ?
        </h3>
        <p className="mt-1 text-sm text-theme-secondary">
          {attachment.kind === 'email'
            ? attachment.emailSubject || 'This email'
            : attachment.fileName || 'This attachment'}{' '}
          will be removed from all linked records. This can't be undone.
        </p>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg border border-theme text-sm text-theme-primary hover:bg-surface-hover disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Delete
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function sourceLabel(type: AttachmentSource['type']): string {
  switch (type) {
    case 'job':
      return 'job';
    case 'deal':
      return 'deal';
    case 'fault':
      return 'fault';
    case 'customer':
      return 'customer';
    case 'customer_service':
      return 'service';
  }
}
